import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  CalendarIcon,
  ClockIcon,
  TagIcon,
  BookOpenIcon,
  LightBulbIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  tags: string[];
  category: string;
  image: string;
}

const blogPosts: BlogPost[] = [
  {
    slug: 'flashcards-o-que-sao-e-como-usar-para-aprender-ingles',
    title: 'Flashcards: o que são e como usar para aprender inglês',
    excerpt: 'Descubra o que são flashcards, como funcionam e por que são a ferramenta mais eficaz para aprender inglês. Crie seus flashcards com IA e acelere seu aprendizado de vocabulário.',
    date: '2025-12-23',
    readTime: '12 min',
    tags: ['Flashcards', 'Inglês', 'Vocabulário', 'Aprendizado de Idiomas', 'IA', 'Repetição Espaçada'],
    category: 'Aprendizado de Idiomas',
    image: '/brain.png'
  },
  {
    slug: 'como-criar-quizzes-com-inteligencia-artificial',
    title: 'Como criar quizzes com inteligência artificial',
    excerpt: 'Descubra como usar IA para criar quizzes personalizados em segundos. Aprenda técnicas avançadas para gerar questões de múltipla escolha, verdadeiro/falso e dissertativas automaticamente.',
    date: '2025-12-12',
    readTime: '12 min',
    tags: ['Quizzes', 'Inteligência Artificial', 'IA', 'Avaliação', 'Técnicas de Estudo', 'Aprendizado Ativo'],
    category: 'Tecnologia e Educação',
    image: '/brain.png'
  },
  {
    slug: 'como-usar-ia-para-estudar-de-forma-eficiente',
    title: 'Como usar a IA para estudar de forma eficiente',
    excerpt: 'Aprenda a usar inteligência artificial para criar flashcards, gerar quizzes personalizados e otimizar sua rotina de estudos. Técnicas práticas e cientificamente comprovadas.',
    date: '2025-11-17',
    readTime: '15 min',
    tags: ['Inteligência Artificial', 'IA', 'Técnicas de Estudo', 'Aprendizado Acelerado', 'Flashcards', 'Produtividade'],
    category: 'Tecnologia e Educação',
    image: '/brain.png'
  },
  {
    slug: 'melhores-metodos-memorizacao-para-concursos',
    title: 'Melhores métodos de memorização para concursos',
    excerpt: 'Domine os métodos de memorização mais eficazes para concursos públicos e aumente suas chances de aprovação usando técnicas científicas comprovadas.',
    date: '2025-11-01',
    readTime: '12 min',
    tags: ['Concursos', 'Memorização', 'Técnicas de Estudo', 'Flashcards', 'Repetição Espaçada'],
    category: 'Métodos de Estudo',
    image: '/brain.png'
  },
  {
    slug: 'como-aprender-qualquer-coisa-mais-rapido',
    title: 'Como aprender qualquer coisa mais rápido',
    excerpt: 'Descubra as estratégias científicas e ferramentas práticas que podem acelerar seu aprendizado em até 3x. Flashcards, quizzes e repetição espaçada ao seu favor.',
    date: '2025-10-28',
    readTime: '10 min',
    tags: ['Aprendizado Acelerado', 'Repetição Espaçada', 'Técnicas de Estudo', 'Produtividade'],
    category: 'Métodos de Estudo',
    image: '/brain.png'
  },
  {
    slug: 'como-memorizar-qualquer-coisa-usando-flashcards',
    title: 'Como memorizar qualquer coisa usando flashcards',
    excerpt: 'Descubra as técnicas científicas por trás dos flashcards e como usar esta poderosa ferramenta para acelerar seu aprendizado em qualquer área do conhecimento.',
    date: '2025-01-17',
    readTime: '8 min',
    tags: ['Memória', 'Técnicas de Estudo', 'Flashcards', 'Aprendizado'],
    category: 'Métodos de Estudo',
    image: '/brain.png'
  },
];

const Blog: React.FC = () => {
  const { isDark } = useTheme();

  useEffect(() => {
    // Update document title for browsers that don't support Helmet
    document.title = 'Blog Upmindy - Técnicas de Estudo e Aprendizado Eficaz';
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Structured data for blog list page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog Upmindy",
    "description": "Descubra técnicas cientificamente comprovadas para acelerar seu aprendizado e maximizar sua retenção de conhecimento",
    "url": "https://upmindy.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Upmindy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://upmindy.com/brain.png"
      }
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "url": `https://upmindy.com/blog/${post.slug}`,
      "image": `https://upmindy.com${post.image}`,
      "keywords": post.tags.join(', '),
      "articleSection": post.category,
      "author": {
        "@type": "Organization",
        "name": "Equipe Upmindy"
      }
    }))
  };

  // Breadcrumb structured data
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://upmindy.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://upmindy.com/blog"
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Blog Upmindy - Técnicas de Estudo e Aprendizado Eficaz</title>
        <meta 
          name="title" 
          content="Blog Upmindy - Técnicas de Estudo e Aprendizado Eficaz" 
        />
        <meta 
          name="description" 
          content="Descubra técnicas cientificamente comprovadas para acelerar seu aprendizado. Artigos sobre flashcards, repetição espaçada, memorização e produtividade nos estudos." 
        />
        <meta 
          name="keywords" 
          content="técnicas de estudo, flashcards, repetição espaçada, memorização, aprendizado eficaz, produtividade, neurociência, métodos de estudo" 
        />
        <link rel="canonical" href="https://upmindy.com/blog" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://upmindy.com/blog" />
        <meta 
          property="og:title" 
          content="Blog Upmindy - Técnicas de Estudo e Aprendizado Eficaz" 
        />
        <meta 
          property="og:description" 
          content="Descubra técnicas cientificamente comprovadas para acelerar seu aprendizado. Artigos sobre flashcards, repetição espaçada, memorização e produtividade nos estudos." 
        />
        <meta property="og:image" content="https://upmindy.com/brain.png" />
        <meta property="og:image:alt" content="Upmindy - Blog de Aprendizado" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Upmindy" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://upmindy.com/blog" />
        <meta 
          property="twitter:title" 
          content="Blog Upmindy - Técnicas de Estudo e Aprendizado Eficaz" 
        />
        <meta 
          property="twitter:description" 
          content="Descubra técnicas cientificamente comprovadas para acelerar seu aprendizado. Artigos sobre flashcards, repetição espaçada, memorização e produtividade nos estudos." 
        />
        <meta property="twitter:image" content="https://upmindy.com/brain.png" />

        {/* Additional SEO tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="Portuguese" />
        <meta name="author" content="Upmindy" />
      </Helmet>

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>

      <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <Navbar />

      {/* Main Content */}
      <main role="main" aria-label="Conteúdo principal">

      {/* Blog Posts */}
      <section className="py-20" aria-label="Artigos do blog">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-center mb-4">Artigos Recentes</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 text-center">
              Conteúdo especializado para revolucionar sua forma de aprender
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <article key={index} className={`rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="aspect-video bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="h-24 w-24 object-contain filter brightness-200"
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 line-clamp-2">
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {post.title}
                    </Link>
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        <time dateTime={post.date}>{formatDate(post.date)}</time>
                      </div>
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span 
                        key={tagIndex}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs"
                      >
                        <TagIcon className="h-3 w-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
                    aria-label={`Ler artigo completo: ${post.title}`}
                  >
                    Ler artigo completo →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white" aria-label="Chamada para ação">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para colocar em prática?
          </h2>
          <p className="text-xl mb-8">
            Use a plataforma Upmindy para aplicar essas técnicas e acelerar seu aprendizado
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register"
              className="px-8 py-4 bg-white text-blue-600 rounded-lg text-lg font-medium hover:bg-gray-100 transition-all transform hover:scale-105"
            >
              Criar Conta Gratuita
            </Link>
            <Link
              to="/docs"
              className="px-8 py-4 border-2 border-white text-white rounded-lg text-lg font-medium hover:bg-white hover:text-blue-600 transition-all"
            >
              Ver Como Funciona
            </Link>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default Blog;
